// A title's badge drawn live (the big view, Oct 19 2026): the game's badge material with the light following the tilt, like
// the Unity app's BadgeTiltDriver (strength 1.5, at most 0.8, smoothing 12/s, x flipped). The tilt comes from the phone
// (deviceorientation — on iPhone after "Tilt with your phone" allows it), else a drag or the mouse over the badge, else a
// slow sway; with reduced motion it stays still unless dragged. The canvas has its own WebGL2 context, let go on close.
import { useEffect, useRef } from "react";
import { BadgeGl } from "@/kit/badgeGl";
import { badgeAssets, badgeHeight, badgeTier, clampTilt, smoothTilt, tiltFromOrientation, TILT } from "@/kit/badgeArt";
import { useMotion } from "./badgeMotion";

export function LiveBadge({ title, tier, onFail }: { title: string; tier?: number | null; onFail: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const motionOn = useMotion((s) => s.on);
  const failRef = useRef(onFail);
  failRef.current = onFail;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let gl: BadgeGl | null = null;
    let raf = 0;
    let stopped = false;
    const t = badgeTier(tier);
    const still = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    let pointer: [number, number] | null = null;
    let phone: [number, number] | null = null;
    let base: [number, number] | null = null;
    let cur: [number, number] = [0, 0];
    let last = performance.now();
    const t0 = last;

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" && e.buttons === 0) return;
      const r = canvas.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 2 - 1;
      const y = -(((e.clientY - r.top) / r.height) * 2 - 1);
      pointer = clampTilt(x * TILT.maxTilt, y * TILT.maxTilt);
      if (still) kick();
    };
    const onEnd = () => {
      pointer = null;
      if (still) kick();
    };
    const onTilt = (e: DeviceOrientationEvent) => {
      if (e.beta == null || e.gamma == null) return;
      if (!base) base = [e.beta, e.gamma];
      phone = tiltFromOrientation(e.beta, e.gamma, base[0], base[1]);
    };

    const frame = (now: number, height: HTMLImageElement) => {
      if (stopped || !gl) return;
      const dt = (now - last) / 1000;
      last = now;
      const sway: [number, number] = still ? [0, 0] : [Math.sin((now - t0) / 1600) * 0.22, Math.sin((now - t0) / 2300) * 0.14];
      const target = pointer ?? phone ?? sway;
      cur = still && !pointer ? [0, 0] : smoothTilt(cur, target, dt);
      gl.draw(height, t, cur);
      if (!still || pointer) raf = requestAnimationFrame((n) => frame(n, height));
    };
    let heightImg: HTMLImageElement | null = null;
    const kick = () => {
      if (!heightImg || stopped) return;
      cancelAnimationFrame(raf);
      last = performance.now();
      raf = requestAnimationFrame((n) => frame(n, heightImg!));
    };

    void Promise.all([badgeAssets(), badgeHeight(title)])
      .then(([{ frame: rings, env }, height]) => {
        if (stopped) return;
        const w = canvas.getBoundingClientRect().width || 200;
        canvas.width = canvas.height = Math.min(768, Math.round(w * Math.min(window.devicePixelRatio || 1, 3)));
        gl = new BadgeGl(canvas, rings, env);
        heightImg = height;
        canvas.addEventListener("webglcontextlost", () => failRef.current());
        kick();
      })
      .catch(() => failRef.current());

    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerdown", onMove);
    canvas.addEventListener("pointerup", onEnd);
    canvas.addEventListener("pointerleave", onEnd);
    canvas.addEventListener("pointercancel", onEnd);
    if (motionOn) window.addEventListener("deviceorientation", onTilt);
    const onVis = () => (document.hidden ? cancelAnimationFrame(raf) : kick());
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerdown", onMove);
      canvas.removeEventListener("pointerup", onEnd);
      canvas.removeEventListener("pointerleave", onEnd);
      canvas.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("deviceorientation", onTilt);
      document.removeEventListener("visibilitychange", onVis);
      gl?.dispose();
    };
  }, [title, tier, motionOn]);

  return <canvas ref={ref} className="tt-live" aria-hidden="true" style={{ touchAction: "none" }} />;
}
